"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import clsx from "clsx";
import { Compass, Home } from "lucide-react";

/** Segundos que se ve el 404 antes de mandar al inicio. */
const REDIRECT_SECONDS = 8;

export interface NotFoundCopy {
  title: string;
  description: string;
  /**
   * Texto de la cuenta regresiva con el placeholder `{seconds}` sin resolver:
   * lo sustituye este componente en cada tic. No puede venir como función
   * porque el 404 de la raíz es un server component y las funciones no
   * atraviesan la frontera hacia el cliente.
   */
  redirect: string;
  cancel: string;
  cancelled: string;
  goHome: string;
}

interface NotFoundContentProps {
  copy: NotFoundCopy;
  /** Inicio al que se redirige: "/es" dentro del segmento de idioma, "/" fuera. */
  homeHref: string;
  /**
   * `true` cuando la pantalla se monta fuera del layout con header fijo
   * (/admin, /kiosko): sin header no hace falta reservarle el espacio de
   * arriba.
   */
  standalone?: boolean;
}

export default function NotFoundContent({
  copy,
  homeHref,
  standalone = false,
}: NotFoundContentProps) {
  const router = useRouter();
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (cancelled) return;
    const tick = setInterval(
      () => setSeconds((prev) => Math.max(0, prev - 1)),
      1000,
    );
    return () => clearInterval(tick);
  }, [cancelled]);

  useEffect(() => {
    if (cancelled || seconds > 0) return;
    // `replace` y no `push`: con push, volver atrás traería de nuevo el 404,
    // la cuenta regresiva arrancaría otra vez y el usuario quedaría atrapado.
    router.replace(homeHref);
  }, [cancelled, seconds, homeHref, router]);

  return (
    <section
      className={clsx(
        // Alto completo siempre: el <main> del layout ya mide min-h-screen,
        // así que una sección más baja dejaba el resto en el gris del body.
        "relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--fondoGeneral-secciones)] px-6 text-center",
        standalone ? "py-20" : "pt-32 pb-20",
      )}
    >
      {/* Mismo patrón de puntos que la pantalla de mantenimiento. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 w-full max-w-lg space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full border border-[var(--botonesSecundarios-iconoLlamativo)]/30 bg-white/5 p-6">
            <Compass
              className="h-14 w-14 text-[var(--botonesSecundarios-iconoLlamativo)]"
              aria-hidden
            />
          </div>
        </div>

        <p className="text-6xl md:text-7xl font-bold tracking-tight text-[var(--botonesSecundarios-iconoLlamativo)]">
          404
        </p>

        <h1 className="text-2xl md:text-3xl font-bold text-white">
          {copy.title}
        </h1>

        <p className="text-slate-300">{copy.description}</p>

        {/* Un solo destino a propósito: desde una URL inventada el historial
            anterior puede ser cualquier cosa, así que "volver atrás" sacaba al
            usuario del sitio en vez de dejarlo en él. */}
        <div className="flex justify-center pt-2">
          {/* El hover va a un dorado más oscuro y no a --hover-interactivos:
              ese token es verde limón (#c2db36) y sobre el dorado canta. */}
          <Link
            href={homeHref}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-[var(--botonesSecundarios-iconoLlamativo)] px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-[#e6a600] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fondoGeneral-secciones)]"
          >
            <Home className="h-4 w-4" aria-hidden />
            {copy.goHome}
          </Link>
        </div>

        <div className="pt-4 space-y-2">
          <p className="text-sm text-slate-400" aria-live="polite">
            {cancelled
              ? copy.cancelled
              : copy.redirect.replace("{seconds}", String(seconds))}
          </p>

          {!cancelled && (
            <>
              {/* Barra de progreso decorativa: el estado ya lo canta el texto. */}
              <div
                aria-hidden
                className="mx-auto h-1 w-48 overflow-hidden rounded-full bg-white/10"
              >
                <div
                  className="h-full bg-[var(--botonesSecundarios-iconoLlamativo)] transition-[width] duration-1000 ease-linear"
                  style={{ width: `${(seconds / REDIRECT_SECONDS) * 100}%` }}
                />
              </div>

              {/* WCAG 2.2.1: un límite de tiempo tiene que poder desactivarse. */}
              <button
                type="button"
                onClick={() => setCancelled(true)}
                className="text-sm text-slate-400 underline underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fondoGeneral-secciones)]"
              >
                {copy.cancel}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
